import { Head, useForm } from '@inertiajs/react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft } from 'lucide-react';
import { Link } from '@inertiajs/react';

interface FilterCategory {
  id: number;
  name: string;
  key: string;
  description: string | null;
  active: boolean;
  position: number;
}

interface Props {
  filter_category: FilterCategory;
  errors?: string[];
}

export default function FilterCategoriesEdit({ filter_category, errors }: Props) {
  const { data, setData, put, processing } = useForm({
    name: filter_category.name,
    key: filter_category.key,
    description: filter_category.description || '',
    active: filter_category.active,
    position: filter_category.position,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put(`/admin/filter_categories/${filter_category.id}`);
  };

  return (
    <AdminLayout>
      <Head title={`Modifier ${filter_category.name}`} />
      
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link href="/admin/filter_categories">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">Modifier {filter_category.name}</h1>
            <p className="text-gray-600 mt-2">
              Modifiez les informations de cette catégorie de filtres
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Informations de la catégorie</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors && errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="text-sm text-red-600">
                    {errors.map((error, index) => (
                      <div key={index}>{error}</div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom de la catégorie *</Label>
                  <Input
                    id="name"
                    value={data.name}
                    onChange={(e) => setData('name', e.target.value)}
                    placeholder="Ex: Spécialités"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="key">Clé technique *</Label>
                  <Input
                    id="key"
                    value={data.key}
                    onChange={(e) => setData('key', e.target.value.toUpperCase())}
                    placeholder="Ex: SPECIALTIES"
                    required
                  />
                  <p className="text-xs text-gray-500">
                    Clé unique utilisée dans le code (en majuscules)
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                  placeholder="Description optionnelle de cette catégorie"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="position">Position</Label>
                  <Input
                    id="position"
                    type="number"
                    value={data.position}
                    onChange={(e) => setData('position', parseInt(e.target.value) || 0)}
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500">
                    Ordre d'affichage (plus petit = plus haut)
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={data.active}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData('active', e.target.checked)}
                  />
                  <Label htmlFor="active">Catégorie active</Label>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Link href="/admin/filter_categories">
                  <Button type="button" variant="outline">
                    Annuler
                  </Button>
                </Link>
                <Button type="submit" disabled={processing}>
                  {processing ? 'Mise à jour...' : 'Mettre à jour'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
} 
